/**
 * Color helpers used to pick readable text and surface colors on top of a CMS defined background color.
 */

export interface RGBColor {
  r: number,
  g: number,
  b: number,
  a: number
}

export interface ReadablePalette {
  /** True when the background is dark and light text should be used */
  isDark: boolean,
  /** Main text color */
  textColor: string,
  /** Secondary text color (subtitles, labels) */
  mutedTextColor: string,
  /** Background behind the text on each card */
  cardColor: string,
  /** Background behind images while they load */
  imageBackgroundColor: string,
  /** Background for buttons and dropdowns */
  controlColor: string,
  /** Border color for buttons, dropdowns and separators */
  borderColor: string,
  /** Opaque surface color for modals and dropdown panels */
  surfaceColor: string
}

/**
 * Parse a CSS color in hex (#rgb, #rgba, #rrggbb, #rrggbbaa) or rgb()/rgba() notation
 * @param {string} color CSS color
 * @returns {RGBColor|null} Parsed color or null if the format is not supported
 */
export const parseColor = (color?: string): RGBColor | null => {
  if (typeof color !== "string") { return null; }
  const value = color.trim();

  if (value.startsWith("#")) {
    let hex = value.slice(1);
    if (hex.length === 3 || hex.length === 4) {
      hex = hex.split("").map(c => c + c).join("");
    }
    if (!/^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(hex)) { return null; }
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
      a: hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1
    };
  }

  const match = value.match(/rgba?\(([^)]+)\)/i);
  if (match) {
    const parts = match[1].split(/[\s,/]+/).filter(Boolean);
    if (parts.length < 3) { return null; }
    const alpha = parts[3] ? (parts[3].endsWith("%") ? parseFloat(parts[3]) / 100 : parseFloat(parts[3])) : 1;
    return { r: Number(parts[0]), g: Number(parts[1]), b: Number(parts[2]), a: isNaN(alpha) ? 1 : alpha };
  }

  return null;
}

/**
 * Relative luminance as defined by WCAG
 * @param {RGBColor} color Color
 * @returns {number} Luminance between 0 and 1
 */
export const getLuminance = (color: RGBColor): number => {
  const [r, g, b] = [color.r, color.g, color.b].map(c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Mix two colors
 * @param {RGBColor} a First color
 * @param {RGBColor} b Second color
 * @param {number} amount Amount of the second color, 0-1
 * @returns {string} CSS rgb color
 */
const mix = (a: RGBColor, b: RGBColor, amount: number): string => {
  const c = (x: number, y: number) => Math.round(x + (y - x) * amount);
  return `rgb(${c(a.r, b.r)}, ${c(a.g, b.g)}, ${c(a.b, b.b)})`;
}

/**
 * Get text, card and surface colors that are readable on top of a background color.
 * Chooses black or white text depending on which gives the best WCAG contrast.
 * Transparent backgrounds are composited on top of black (the app's body color).
 * @param {string} backgroundColor CSS background color
 * @param {string} fallbackColor Used if the background color can not be parsed
 * @returns {ReadablePalette} Colors to use
 */
export const getReadablePalette = (backgroundColor?: string, fallbackColor: string = "#1E2A36"): ReadablePalette => {
  const parsed = parseColor(backgroundColor) || parseColor(fallbackColor) || { r: 30, g: 42, b: 54, a: 1 };
  const white = { r: 255, g: 255, b: 255, a: 1 };

  // Composite on top of black if the color has transparency
  const bg: RGBColor = {
    r: Math.round(parsed.r * parsed.a),
    g: Math.round(parsed.g * parsed.a),
    b: Math.round(parsed.b * parsed.a),
    a: 1
  };

  const lum = getLuminance(bg);
  const contrastWithWhite = 1.05 / (lum + 0.05);
  const contrastWithBlack = (lum + 0.05) / 0.05;
  const isDark = contrastWithWhite >= contrastWithBlack;

  if (isDark) {
    return {
      isDark,
      textColor: "#ffffff",
      mutedTextColor: "rgba(255, 255, 255, 0.72)",
      cardColor: "rgba(255, 255, 255, 0.08)",
      imageBackgroundColor: "rgba(255, 255, 255, 0.12)",
      controlColor: "rgba(255, 255, 255, 0.06)",
      borderColor: "rgba(255, 255, 255, 0.28)",
      surfaceColor: mix(bg, white, 0.08)
    };
  }

  return {
    isDark,
    textColor: "#111111",
    mutedTextColor: "rgba(0, 0, 0, 0.64)",
    cardColor: "rgba(0, 0, 0, 0.06)",
    imageBackgroundColor: "rgba(0, 0, 0, 0.08)",
    controlColor: "rgba(0, 0, 0, 0.03)",
    borderColor: "rgba(0, 0, 0, 0.24)",
    surfaceColor: mix(bg, white, 0.6)
  };
}

/**
 * Use another background behind images than the palette's (e.g. from a view's settings)
 * @param {ReadablePalette} palette Palette
 * @param {string} imageBackgroundColor Background behind images. Undefined keeps the palette's
 * @returns {ReadablePalette} Palette
 */
export const withImageBackground = (palette: ReadablePalette, imageBackgroundColor?: string): ReadablePalette =>
  imageBackgroundColor ? { ...palette, imageBackgroundColor } : palette;
